import React, { useEffect, useCallback, useRef, useState, useMemo } from "react";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { useHoldAndWinControl } from "../../../../Contexts/HoldAndWinControlContext";
import { useReelRefs } from "../../../../Contexts/ReelRefsContext";
import { useHoldAndWinCollectionController } from "../../../../Hooks/useHoldAndWinCollectionController";
import {
  selectFeatureSpinActive,
  selectSpacebarSpinEnabled,
  selectShowHoldAndWinCollector,
  selectHoldAndWinActive,
  selectShowHoldAndWinOutro,
} from "../../../../State/selectors";
import store from "../../../../State/Store/store";
import {
  createButtonPressAnimation,
  createPlayButtonRotationAnimation,
} from "../../../../utilities";
import { getUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";

// Hold and win play button configuration - matches UI button dimensions
const HOLD_AND_WIN_PLAY_BUTTON_CONFIG = {
  BASE_WIDTH: 170,
  BASE_HEIGHT: 170,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.9,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.6,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.25,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.675,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// Simple action tracking
let lastActionTime = 0;
const MIN_ACTION_GAP = 100; // Increased from 60ms to 100ms for better throttling (~10 clicks/sec max)

// EVENT-LEVEL THROTTLE: Blocks click handler execution at entry point
// Prevents performance degradation from rapid clicking (50+ clicks/sec)
let clickThrottleActive = false;
let clickThrottleTimeout = null;

// ONE-SHOT ACTION GATE: Prevents multiple stop actions from rapid clicking
// Once stop is triggered, ALL subsequent clicks are blocked until spin completes
let stopActionTriggered = false;

// ONE-SHOT COLLECTION SKIP GATE: Prevents multiple skip triggers during collection
// Once skip is triggered, button becomes DEAD until collection ends
let collectionSkipTriggered = false;

// Spacebar state tracking (simple tap only, no hold)
let spacePressed = false;

/**
 * Simplified play/stop button for HoldAndWin feature game
 *
 * Features:
 * - Simple spin/stop functionality (no turbo, autoplay, or hold)
 * - Same visual styling and effects as regular play button
 * - Uses HoldAndWin-specific states and controls
 */
const ButtonHoldAndWinPlay = ({ toolbar }) => {
  const [actionInProgress, setActionInProgress] = useState(false);
  const [stopButtonDisabled, setStopButtonDisabled] = useState(false);

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);
  // Track if press animation is currently running (prevents rapid click shrinking)
  const isPressAnimatingRef = useRef(false);
  // Single shared timeout for flash effect (prevents buildup)
  const flashTimeoutRef = useRef(null);
  const { playButtonRef } = useReelRefs();
  const { dimensions, uiSymbolSize, device, orientation, getResponsiveConfig } =
    useDimensions();

  // Refs for latest state access in event handlers
  const setActionInProgressRef = useRef(setActionInProgress);
  const setStopButtonDisabledRef = useRef(setStopButtonDisabled);

  const { handleSpin, handleManualStop } = useHoldAndWinControl();
  const { triggerCollectionSkip } = useHoldAndWinCollectionController();

  // Refs for latest values
  const handleSpinRef = useRef(handleSpin);
  const handleManualStopRef = useRef(handleManualStop);
  const actionInProgressRef = useRef(actionInProgress);
  const stopButtonDisabledRef = useRef(stopButtonDisabled);

  // Update refs when values change
  useEffect(() => {
    handleSpinRef.current = handleSpin;
    handleManualStopRef.current = handleManualStop;
    actionInProgressRef.current = actionInProgress;
    stopButtonDisabledRef.current = stopButtonDisabled;
    setActionInProgressRef.current = setActionInProgress;
    setStopButtonDisabledRef.current = setStopButtonDisabled;
  }, [
    handleSpin,
    handleManualStop,
    actionInProgress,
    stopButtonDisabled,
    setActionInProgress,
    setStopButtonDisabled,
  ]);

  // Redux state selectors
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const featureSpinActive = useSelector(selectFeatureSpinActive);
  const spacebarSpinEnabled = useSelector(selectSpacebarSpinEnabled);
  const showHoldAndWinCollector = useSelector(selectShowHoldAndWinCollector);
  const showHoldAndWinOutro = useSelector(selectShowHoldAndWinOutro);

  // Track previous spinning state to detect transitions
  const prevIsSpinningRef = useRef(featureSpinActive);

  // Reset button state when Hold and Win feature starts (ensures clean state for each session)
  useEffect(() => {
    if (holdAndWinActive) {
      setActionInProgress(false);
      setStopButtonDisabled(false);
      prevIsSpinningRef.current = false;
      // CRITICAL: Reset one-shot gate when feature starts
      stopActionTriggered = false;
    }
  }, [holdAndWinActive]);

  // Reset action states when spin completes (transition from spinning to not spinning)
  useEffect(() => {
    // Detect transition from spinning to not spinning
    if (prevIsSpinningRef.current && !featureSpinActive) {
      setActionInProgress(false);
      setStopButtonDisabled(false);
      // CRITICAL: Reset one-shot gate when spin completes
      stopActionTriggered = false;
    }

    // Update previous state
    prevIsSpinningRef.current = featureSpinActive;
  }, [featureSpinActive]);

  // Reset collection skip gate when collection ends
  useEffect(() => {
    if (!showHoldAndWinCollector) {
      // Collection ended - unlock button by resetting the gate
      collectionSkipTriggered = false;
    }
  }, [showHoldAndWinCollector]);

  /**
   * Resolve what action the button should perform
   * Hold and Win uses autoplay - only allow stopping, not starting
   */
  const resolveButtonAction = useCallback(() => {
    const state = store.getState();

    // Block ALL actions if turbo mode is active (button visible but unresponsive)
    // Turbo mode plays on its own - no manual actions allowed
    if (state.settings.autoPlaySpeed === "turbo") {
      return {
        type: "NO_OP",
      };
    }

    // Stop spin if spinning
    if (state.spin.featureSpinActive) {
      return {
        type: "STOP_SPIN",
      };
    }

    // Don't allow manual start - Hold and Win uses autoplay
    return {
      type: "DISABLED",
    };
  }, []);

  /**
   * Execute stop spin action
   */
  const executeStopSpin = useCallback(() => {
    soundManager.play(SOUND_NAME.button_click);
    lastActionTime = Date.now();
    setActionInProgressRef.current(true);
    setStopButtonDisabledRef.current(true);
    if (handleManualStopRef.current) {
      handleManualStopRef.current();
    }
    return true;
  }, []);

  /**
   * Execute start spin action
   * HoldAndWin is a free feature round - no balance/bet checks needed
   */
  const executeStartSpin = useCallback(() => {
    soundManager.play(SOUND_NAME.spin);
    lastActionTime = Date.now();
    setActionInProgress(true);
    if (handleSpinRef.current) {
      handleSpinRef.current();
    }
    return true;
  }, [setActionInProgress]);

  /**
   * Flash brightness effect on click/tap (visual feedback)
   * Uses single shared timeout to prevent buildup at high click rates
   */
  const flashBrightness = useCallback(() => {
    if (!playButtonRef.current) return;

    // Clear any existing timeout (prevents buildup - only 1 timeout max)
    if (flashTimeoutRef.current) {
      clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = null;
    }

    // Apply brightness filter
    playButtonRef.current.filters = [brightnessFilter.current];

    // Single timeout that gets reset on each click (no buildup)
    flashTimeoutRef.current = setTimeout(() => {
      if (playButtonRef.current && !isHoveredRef.current) {
        playButtonRef.current.filters = [];
      }
      flashTimeoutRef.current = null;
    }, 120);
  }, [playButtonRef]);

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(HOLD_AND_WIN_PLAY_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Calculate button radius to match the visual sprite exactly - memoized
  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width]
  );

  /**
   * Master action executor
   */
  const executeButtonAction = useCallback(
    (action, source = "click") => {
      // Don't do anything if button is disabled or no-op (turbo mode)
      if (action.type === "DISABLED" || action.type === "NO_OP") {
        return;
      }

      // Visual feedback already handled at top of handleClick
      // For START_SPIN, add rotation animation
      if (action.type === "START_SPIN" && playButtonRef.current) {
        createPlayButtonRotationAnimation(playButtonRef.current);
      }

      // For spacebar, add visual feedback here
      if (source === "spacebar" && action.type !== 'START_SPIN') {
        if (playButtonRef.current && !isPressAnimatingRef.current) {
          isPressAnimatingRef.current = true;
          createButtonPressAnimation(playButtonRef.current, {
            targetWidth: buttonConfig.width,
            targetHeight: buttonConfig.height,
            onComplete: () => {
              isPressAnimatingRef.current = false;
            }
          });
        }
      }

      switch (action.type) {
        case "STOP_SPIN":
          executeStopSpin();
          break;
        case "START_SPIN":
          executeStartSpin();
          break;
        default:
          // Unknown action type or disabled
          break;
      }
    },
    [executeStopSpin, executeStartSpin, playButtonRef, buttonConfig.width, buttonConfig.height]
  );

  /**
   * Handle click function
   * If collector is showing, skip collection animation
   * Otherwise: start spin if not spinning, stop spin if spinning
   */
  const handleClick = useCallback(() => {
    // EVENT-LEVEL THROTTLE: FIRST LINE - Instant return if throttle is active
    // Prevents performance degradation from rapid clicking (50+ clicks/sec)
    if (clickThrottleActive) {
      return; // Instant exit - zero overhead, no logs, no code execution
    }

    // Set throttle flag IMMEDIATELY and schedule single reset
    clickThrottleActive = true;
    if (clickThrottleTimeout) {
      clearTimeout(clickThrottleTimeout);
    }
    clickThrottleTimeout = setTimeout(() => {
      clickThrottleActive = false;
      clickThrottleTimeout = null;
    }, MIN_ACTION_GAP);

    // EARLY TURBO CHECK: Skip all expensive work if turbo mode is active
    // This check happens before most state reads and action resolution
    const quickState = store.getState();
    if (quickState.settings.autoPlaySpeed === "turbo") {
      flashBrightness(); // Only provide visual feedback, no other work
      return;
    }

    // CRITICAL: Block ALL interactions when outro dialog is showing
    // Prevents clicks during component unmount/transition phase
    if (showHoldAndWinOutro) {
      return;
    }

    // If collector is showing, skip collection animation (allow this action even when button would be disabled)
    if (showHoldAndWinCollector) {
      // ONE-SHOT GATE: Only allow ONE skip trigger per collection
      // After first click, button becomes DEAD until collection ends
      if (collectionSkipTriggered) {
        return; // Button is dead - no action at all
      }
      
      // First click during collection - trigger skip via keyboard event and lock button
      // The hook's keyboard handler is the proper way to skip collection
      collectionSkipTriggered = true;
      
      const spacebarEvent = new KeyboardEvent('keydown', {
        code: 'Space',
        key: ' ',
        keyCode: 32,
        bubbles: true,
        cancelable: true
      });
      document.dispatchEvent(spacebarEvent);
      
      return;
    }

    // Determine what action will be taken
    const action = resolveButtonAction();

    // CRITICAL: Block DISABLED actions immediately - no animations, no effects, button is dead
    if (action.type === 'DISABLED') {
      return;
    }

    // ALWAYS provide visual feedback - except for START_SPIN (has its own rotation animation)
    if (action.type !== 'START_SPIN') {
      soundManager.play(SOUND_NAME.button_click);
      // Only start new animation if one isn't already running (debouncing)
      if (playButtonRef.current && !isPressAnimatingRef.current) {
        isPressAnimatingRef.current = true;
        createButtonPressAnimation(playButtonRef.current, {
          targetWidth: buttonConfig.width,
          targetHeight: buttonConfig.height,
          onComplete: () => {
            isPressAnimatingRef.current = false;
          }
        });
      }
      flashBrightness();
    }

    const isSpinning = quickState.spin.featureSpinActive;

    // Check for critical animation (expanding wilds, etc.)
    if (quickState.animations.criticalAnimationPlaying) {
      return; // Visual feedback already played
    }

    if (quickState.userInterface.userInterfaceDisabled) {
      return; // Visual feedback already played
    }

    // Block clicks if stop button is disabled (after stop is pressed)
    if (stopButtonDisabledRef.current) {
      return; // Visual feedback already played
    }

    // Allow clicks during spin (for manual stop), but block if not spinning and actionInProgress (prevent double-clicks)
    if (actionInProgressRef.current && !isSpinning) {
      return; // Visual feedback already played
    }

    const now = Date.now();
    if (now - lastActionTime < MIN_ACTION_GAP) {
      return; // Visual feedback already played
    }

    // ONE-SHOT GATE: If this is a STOP action, check if stop was already triggered
    // This prevents rapid clicking from triggering multiple stops
    if (action.type === 'STOP_SPIN') {
      if (stopActionTriggered) {
        return; // Stop already triggered, ignore all subsequent clicks
      }
      // Mark stop as triggered - will be reset when spin completes
      stopActionTriggered = true;
    }

    executeButtonAction(action, "click");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showHoldAndWinCollector,
    showHoldAndWinOutro,
    triggerCollectionSkip,
    resolveButtonAction,
    executeButtonAction,
    flashBrightness,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  // Calculate responsive button position based on device and orientation - memoized
  // Uses toolbar width instead of full screen width
  const buttonPosition = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y: -dimensions.height / 5.0,
        };
      } else {
        // landscape
        return {
          x: toolbar.width - buttonConfig.width / 1.65,
          y: -dimensions.height / 2 + buttonConfig.height / 2.0,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2,
          y: -dimensions.height / 5.0,
        };
      } else {
        // landscape
        return {
          x: toolbar.width - buttonConfig.width / 1.4,
          y: toolbar.height - buttonConfig.height / 1.7,
        };
      }
    } else {
      const yOffset = 0.04;
      return {
        x: toolbar.width - buttonConfig.width * 1.275,
        y: uiSymbolSize * yOffset,
      };
    }
  }, [device, orientation, toolbar.width, toolbar.height, dimensions.height, buttonConfig.width, buttonConfig.height, uiSymbolSize]);

  // Get the appropriate icon texture - always use play icon
  const iconTexture = getUITexture("icon_play");

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    // Always allow hover effect for better UX
    if (playButtonRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !playButtonRef.current.filters ||
        playButtonRef.current.filters.length === 0
      ) {
        playButtonRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [playButtonRef, brightnessFilter]);

  const handlePointerOut = useCallback(() => {
    if (playButtonRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      playButtonRef.current.filters = [];
    }
  }, [playButtonRef]);

  // Memoize draw function for Graphics component
  const drawInteractiveArea = useCallback(
    (g) => {
      g.clear();
      // Nearly invisible fill (required for hit detection to work)
      g.beginFill(0x000000, 0.01);
      g.drawCircle(0, 0, buttonRadius);
      g.endFill();
    },
    [buttonRadius]
  );


  // Prevent context menu on long press/touch-and-hold
  useEffect(() => {
    const preventContextMenu = (event) => {
      event.preventDefault();
      return false;
    };

    document.addEventListener("contextmenu", preventContextMenu, {
      passive: false,
    });
    return () =>
      document.removeEventListener("contextmenu", preventContextMenu);
  }, []);

  // Cleanup animations and timeouts when component unmounts
  useEffect(() => {
    return () => {
      // Reset press animation flag when component unmounts
      // The animation utility will handle stopping animations if sprite is destroyed
      isPressAnimatingRef.current = false;
      
      // Clear flash timeout to prevent memory leaks
      if (flashTimeoutRef.current) {
        clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = null;
      }
    };
  }, []);

  // Keyboard event handling - spacebar tap (no hold/turbo logic)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // EVENT-LEVEL THROTTLE: Instant return if throttle is active
      // Prevents performance degradation from rapid spacebar spam
      if (clickThrottleActive) {
        return; // Instant exit - zero overhead
      }

      // Set throttle flag IMMEDIATELY and schedule single reset
      clickThrottleActive = true;
      if (clickThrottleTimeout) {
        clearTimeout(clickThrottleTimeout);
      }
      clickThrottleTimeout = setTimeout(() => {
        clickThrottleActive = false;
        clickThrottleTimeout = null;
      }, MIN_ACTION_GAP);

      // Block spacebar if disabled in settings
      if (!spacebarSpinEnabled) {
        return;
      }

      // CRITICAL: Block ALL interactions when outro dialog is showing
      // Prevents spacebar during component unmount/transition phase
      if (showHoldAndWinOutro) {
        return;
      }

      event.preventDefault();

      const state = store.getState();

      // ALWAYS provide flash feedback (even in turbo) - no timeout, just filter
      flashBrightness();

      // Block ALL actions if turbo mode is active (button visible but unresponsive)
      // Turbo mode plays on its own - no manual actions allowed
      if (state.settings.autoPlaySpeed === "turbo") {
        return;
      }

      // If collector is showing, skip collection animation
      if (showHoldAndWinCollector) {
        triggerCollectionSkip();
        spacePressed = false;
        return;
      }

      // Determine what action will be taken
      const action = resolveButtonAction();

      // CRITICAL: Block DISABLED actions immediately - no animations, no effects, button is dead
      if (action.type === 'DISABLED') {
        return;
      }

      // ALWAYS provide visual feedback - except for START_SPIN (has its own rotation animation)
      if (action.type !== 'START_SPIN' && playButtonRef.current && !isPressAnimatingRef.current) {
        isPressAnimatingRef.current = true;
        createButtonPressAnimation(playButtonRef.current, {
          targetWidth: buttonConfig.width,
          targetHeight: buttonConfig.height,
          onComplete: () => {
            isPressAnimatingRef.current = false;
          }
        });
      }

      // Check for critical animation (expanding wilds, etc.)
      if (state.animations.criticalAnimationPlaying) {
        return;
      }

      if (state.userInterface.userInterfaceDisabled) {
        return;
      }

      // Block spacebar if stop button is disabled (after stop is pressed)
      if (stopButtonDisabledRef.current) {
        return;
      }

      // Allow spacebar during spin (for manual stop), but block if not spinning and actionInProgress
      const isSpinning = state.spin.featureSpinActive;
      if (actionInProgressRef.current && !isSpinning) {
        return;
      }

      const now = Date.now();
      if (now - lastActionTime < MIN_ACTION_GAP) {
        return;
      }

      // Track spacebar press (simple tap only, no hold timer)
      if (!spacePressed) {
        spacePressed = true;
      }
    };

    const handleKeyUp = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Block spacebar if disabled in settings
      if (!spacebarSpinEnabled) {
        return;
      }

      if (!spacePressed) return;

      const state = store.getState();

      // Check for critical animation (expanding wilds, etc.)
      if (state.animations.criticalAnimationPlaying) {
        spacePressed = false;
        return;
      }

      // Block spacebar if stop button is disabled (after stop is pressed)
      if (stopButtonDisabledRef.current) {
        spacePressed = false;
        return;
      }

      // Allow spacebar during spin (for manual stop), but block if not spinning and actionInProgress
      const isSpinning = state.spin.featureSpinActive;
      if (actionInProgressRef.current && !isSpinning) {
        spacePressed = false;
        return;
      }

      // Reset spacebar state
      spacePressed = false;

      // Execute action on spacebar release (simple tap)
      const now = Date.now();
      if (now - lastActionTime < MIN_ACTION_GAP) {
        return;
      }

      const action = resolveButtonAction();
      executeButtonAction(action, "spacebar");
    };

    const handleWindowBlur = () => {
      // Cleanup on window blur
      spacePressed = false;
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleWindowBlur);
      spacePressed = false;
      
      // Cleanup throttle timeout
      if (clickThrottleTimeout) {
        clearTimeout(clickThrottleTimeout);
        clickThrottleTimeout = null;
        clickThrottleActive = false;
      }
    };
  }, [
    spacebarSpinEnabled,
    showHoldAndWinCollector,
    showHoldAndWinOutro,
    triggerCollectionSkip,
    resolveButtonAction,
    executeButtonAction,
    flashBrightness,
    playButtonRef,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  return (
    <Container x={buttonPosition.x} y={buttonPosition.y}>
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="hold_and_win_play_button_interactive_area"
        eventMode="static"
        cursor="pointer"
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        onpointertap={handleClick}
        draw={drawInteractiveArea}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      <Sprite
        ref={playButtonRef}
        texture={iconTexture}
        name="hold_and_win_play_stop_button"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        x={0}
        y={0}
        eventMode="none"
        alpha={1}
      />
    </Container>
  );
};

export default ButtonHoldAndWinPlay;
